import { DynamicPriceIcon, J1RightarrowD, J2Multiply } from '@e3dc-react/icons'
import { LineChartSkeleton } from '@e3dc-react/shell/Elements/Skeletons'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Box, IconButton, Stack, Typography } from '@mui/material'
import React from 'react'
import { DynamicPowerPriceWidgetData } from 'src/Libs/dashboard-widgets/hooks/use-data-for-dpp-widget'
import { makeStyles } from 'tss-react/mui'
import { PowerPriceChart, PowerPriceChartLabel } from './power-price-chart'
import { DASHBOARD_DYNAMIC_POWER_PRICE_WIDGET_ID } from 'src/Libs/qa/ids'
import DynamicEmsWidgetSubtitle from './DynamicEmsWidgetSubtitle'
import { WIDGET_HEIGHT } from '../base-card/const'
import { DynamicEmsData } from 'src/Libs/dynamic-power-prices/types'
import { toDate } from '../../utils/dateUtils'
import ChartBlankView from 'src/Libs/chart-kit/shared/components/ChartBlankView'
import { useIsDynamicEmsAccessible } from 'src/Libs/dynamic-power-prices'

export type DynamicPowerPriceWidgetProps = DynamicPowerPriceWidgetData & {
  handleOnClick: () => void
  handleOpenPowerPriceChartModal: () => void
  isDynamicPowerPriceEnabledForBattery: boolean
  isDynamicPowerPriceEnabledForWallboxes: boolean
  data: DynamicEmsData[]
  isDynamicEmsDataLoading: boolean
  wallboxLimit: number
  batteryLimit: number
}

const CUSTOM_XS_BREAKPOINT = 400

const useStyles = makeStyles()(theme => ({
  container: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.effectStyles.shadowMain,
    flex: '1 1 200px',
    minHeight: WIDGET_HEIGHT,
    cursor: 'pointer',
    minWidth: 200,
    width: '100%',
    [theme.breakpoints.down('sm')]: {
      minHeight: 'unset',
      minWidth: 'unset',
      flex: 'unset',
    },
  },
  titleWrapper: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    width: '100%',
    padding: theme.spacing(2),
  },
  title: {
    marginBottom: 0,
    marginRight: theme.spacing(1),

    [theme.breakpoints.down(CUSTOM_XS_BREAKPOINT)]: {
      maxWidth: 80,
      marginRight: 0,
    },
  },
  link: {
    display: 'none',
    color: theme.palette.grey.grey00,
    padding: 0,
    marginLeft: 'auto',

    [theme.breakpoints.up('sm')]: {
      display: 'block',
    },
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    overflow: 'hidden',
    width: '100%',
  },
  chartLabel: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'baseline',
    marginLeft: theme.spacing(2),
  },
  chartWrapper: {
    position: 'relative',
    width: '100%',
    height: '100%',
    padding: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2),
      paddingTop: theme.spacing(1),
      minHeight: 80,
    },
  },
  noDataSubtitle: {
    display: 'flex',
    alignItems: 'center',
    whiteSpace: 'nowrap',

    '& > svg': {
      fontSize: 12,
      marginLeft: 0.5,
    },
  },
}))

/**
 * Component for a chart widget that shows the variable dynamic power price
 */
const DynamicPowerPriceWidget: React.FC<DynamicPowerPriceWidgetProps> = ({
  powerPricesForWidget,
  currentPowerPriceValues,
  dynamicPricesSettings,
  handleOnClick,
  handleOpenPowerPriceChartModal,
  isDynamicPowerPriceEnabledForBattery,
  isDynamicPowerPriceEnabledForWallboxes,
  data,
  isDynamicEmsDataLoading,
  isDynamicPowerPriceWidgetDataLoading,
  wallboxLimit,
  batteryLimit,
}) => {
  const { t } = useCustomTranslation()
  const { classes } = useStyles()
  const { isDynamicEmsV2Accessible } = useIsDynamicEmsAccessible()

  const isDynamicEmsEnabled = dynamicPricesSettings?.isDynamicEmsEnabledForBattery

  const isBothDynamicPowerPriceEnabled = isDynamicPowerPriceEnabledForBattery && isDynamicPowerPriceEnabledForWallboxes
  const iconColor =
    (isBothDynamicPowerPriceEnabled && currentPowerPriceValues.price > wallboxLimit && currentPowerPriceValues.price > batteryLimit) ||
    (!isBothDynamicPowerPriceEnabled && isDynamicPowerPriceEnabledForWallboxes && currentPowerPriceValues.price > wallboxLimit) ||
    (!isBothDynamicPowerPriceEnabled && isDynamicPowerPriceEnabledForBattery && currentPowerPriceValues.price > batteryLimit)
      ? 'warning'
      : 'primary'
  const title = t('dashboard.liveView.widgets.dynamic-power-price.title')

  const handleOnClickContainer = (): void => {
    handleOnClick()
    handleOpenPowerPriceChartModal()
  }

  const isNoDataAvailable = !isDynamicPowerPriceWidgetDataLoading && (!powerPricesForWidget || powerPricesForWidget.length === 0)

  return (
    <Box className={classes.container} onClick={handleOnClickContainer} data-qa={DASHBOARD_DYNAMIC_POWER_PRICE_WIDGET_ID}>
      <Box className={classes.content}>
        <Box className={classes.titleWrapper}>
          <Stack>
            <Box
              sx={{
                display: 'flex',
              }}
            >
              <Typography variant={'h3'} className={classes.title} noWrap>
                {title}
              </Typography>
              {!isDynamicEmsEnabled && <DynamicPriceIcon color={iconColor} sx={{ fontSize: 20 }} />}
            </Box>
            {isNoDataAvailable ? (
              <Typography variant="caption" className={classes.noDataSubtitle}>
                {t('dashboard.liveView.widgets.dynamic-power-price.no-data-subtitle')} {isNoDataAvailable && <J2Multiply />}
              </Typography>
            ) : (
              isDynamicEmsEnabled &&
              !isDynamicEmsV2Accessible && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: '100%', flex: 1 }}>
                  <DynamicEmsWidgetSubtitle dynamicEmsData={data} isDynamicEmsDataLoading={isDynamicEmsDataLoading} />
                </Box>
              )
            )}
          </Stack>
          <IconButton className={classes.link}>
            <J1RightarrowD />
          </IconButton>
        </Box>
        {isNoDataAvailable ? (
          <Box sx={{ padding: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <ChartBlankView />
          </Box>
        ) : powerPricesForWidget && !isDynamicPowerPriceWidgetDataLoading ? (
          <>
            <PowerPriceChartLabel price={currentPowerPriceValues?.price} className={classes.chartLabel} />
            <Box className={classes.chartWrapper} onClick={handleOpenPowerPriceChartModal}>
              <PowerPriceChart
                to={toDate.toISOString()}
                powerPrices={powerPricesForWidget}
                wallboxLimit={wallboxLimit}
                currentPowerPriceValues={currentPowerPriceValues}
                batteryLimit={batteryLimit}
                isDynamicEmsEnabled={isDynamicEmsEnabled}
                dynamicEmsData={data}
                isDynamicEmsDataLoading={isDynamicEmsDataLoading}
                isDynamicPowerPriceEnabledForBattery={isDynamicPowerPriceEnabledForBattery}
                isDynamicPowerPriceEnabledForWallboxes={isDynamicPowerPriceEnabledForWallboxes}
              />
            </Box>
          </>
        ) : (
          <LineChartSkeleton />
        )}
      </Box>
    </Box>
  )
}

export default DynamicPowerPriceWidget
